import React, { useState } from 'react';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { readFileSync } from 'fs';
import pathsData from '../../../buildArtifacts/paths';
import ScrollTopArrow from '../../../components/ScrollTopArrow';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PageCourseNavigation from '../../../components/PageCourseNavigation';
import PageCoursePropTypes from '../../../components/PageCourse/PageCoursePropTypes';
import PageCourseTopic from '../../../components/PageCourseTopic';
import PageCourseTopicPropTypes from '../../../components/PageCourseTopic/PageCourseTopicPropTypes';
import getThemeClass from '../../../utils/getThemeClass';
import getBreadcrumb from '../../../utils/getBreadcrumb';
import tidySeo from '../../../utils/tidySeo';
import getSeoPageTitle from '../../../utils/getSeoPageTitle';
import getCoveoSearchMeta from '../../../utils/getCoveoSearchMeta';
import getCanonicalUrl from '../../../utils/getCanonicalUrl';
import getSettings from '../../../buildArtifacts/settings';

const { pageCourseTopicNested: paths } = pathsData;

export const pageCourseTopicPropTypes = {
  pageData: PropTypes.shape(PageCourseTopicPropTypes).isRequired,
  courseData: PropTypes.shape(PageCoursePropTypes).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  seo: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  breadcrumbs: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageTitle: PropTypes.string,
  canonicalUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  settingsGlobal: PropTypes.object.isRequired
};

function CourseTopicPage({ pageData, seo = {}, courseData, breadcrumbs, pageTitle = '', canonicalUrl, settingsGlobal, allcourses }) {
  const [isNavVisible, setIsNavVisible] = useState(true);
  const {
    _id,
    _contentTypeId,
    _as,
    _href,
    slug,
    eyebrow,
    name,
    description,
    publishDate,
    content,
    readingTime,
    jsonLdSchema,
    showToC,
  } = pageData;
  return (
    <>
      <Layout seo={seo} showFooter pageTitle={pageTitle} canonicalUrl={canonicalUrl} settingsGlobal={settingsGlobal}>
        <Breadcrumb breadcrumbs={breadcrumbs} parentPage="pageCourseTopic" />
        {/* <ScrollTopArrow /> */}


        <PageCourseTopic
          _id={_id}
          _contentTypeId={_contentTypeId}
          _as={_as}
          _href={_href}
          course={courseData}
          slug={slug}
          eyebrow={eyebrow}
          name={name}
          description={description}
          publishDate={publishDate}
          content={content}
          readingTime={readingTime}
          markCompletedOnScrollToBottom
          showToC={showToC}
          allcourses={allcourses}
        />
        {isNavVisible &&
          <PageCourseNavigation
            courseId={courseData._id}
            courseAs={courseData._as}
            courseHref={courseData._href}
            courseName={courseData.name}
            topics={courseData.topics}
            topic={pageData}
            progressClassName={`theme-${getThemeClass(courseData, courseData.category)}`}
          />}
        {jsonLdSchema && !isEmpty(jsonLdSchema) && (
          // eslint-disable-next-line react/no-danger
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }} />
        )}
      </Layout>
    </>
  );
}

// This function gets called at build time
export const getStaticPaths = async ({ locales }) => ({
  paths: flatMap(locales, (locale) => paths.map((pathObject) => ({ ...pathObject, locale }))),
  fallback: false
});

export const getStaticProps = async ({ locale, params }) => {
  const settingsGlobal = getSettings(locale);
  const { slug, topicslug } = params;

  const pageCourseData = JSON.parse(
    readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageCourse/${slug}.json`, 'utf8')
  );

  const pageCourseSeo = tidySeo(get(pageCourseData, 'seo', {}));

  const pageData = JSON.parse(
    readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageCourseTopic/${topicslug}.json`, 'utf8')
  );
  const AllCourses = JSON.parse(readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageLanding/self-guided-learning.json`, 'utf8'));
  const pageDataSeo = tidySeo(get(pageData, 'seo', {}));
  const pageTitle = getSeoPageTitle(pageDataSeo, pageData);

  delete pageCourseSeo.title;

  // Checks if pageCourse has noindex set   If not, then will allow for it to fallback to the topics indexing rules
  if (pageCourseSeo?.robots?.value !== 'noindex,nofollow') delete pageCourseSeo.robots;

  delete pageDataSeo.title;

  const pageCoveoSearchMeta = getCoveoSearchMeta(pageData, locale);
  const seo = { ...pageDataSeo, ...pageCoveoSearchMeta };
  seo.content_type = { name: 'content_type', value: pageData._contentTypeId };
  seo.lang = { name: 'lang', value: locale }

  const canonicalUrl = getCanonicalUrl(pageData, locale, `self-guided-learning/${slug}/${topicslug}`);
  const pagebreadcrumb = {
    as: "/self-guided-learning",
    href: "/self-guided-learning",
    text: "Self-guided learning"
  }
  const breadcrumbs = [pagebreadcrumb, getBreadcrumb(pageCourseData), getBreadcrumb(pageData, pageCourseData)];

  return {
    props: {
      pageData,
      courseData: pageCourseData,
      seo,
      breadcrumbs,
      pageTitle,
      canonicalUrl,
      settingsGlobal,
      allcourses: AllCourses
    }
  };
};

CourseTopicPage.propTypes = pageCourseTopicPropTypes;

export default CourseTopicPage;